"use client";

import { Content } from "@/services/content/types";
import {
  INFINITE_SCROLL_ARTICLE_LIMIT,
  INFINITE_SCROLL_ITEMS_PER_FETCH,
} from "@/utils/constants";
import * as kilkaya from "@/utils/kilkaya";
import { sendGTMEvent } from "@next/third-parties/google";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import OnceInView from "./once-in-view";

const NewsletterForm = dynamic(() => import("./newsletter-form"));

type ArticlePageFeedProps = {
  initalArticle: {
    jsx: JSX.Element;
    data: Content.Article;
  };
  loadMore: (
    page: number,
    articleIdsToExclude: string[],
  ) =>
    | Promise<
        {
          jsx: JSX.Element;
          data: Content.Article;
        }[]
      >
    | undefined;
};

const ArticlePageFeed = ({ initalArticle, loadMore }: ArticlePageFeedProps) => {
  const [articles, setArticles] = useState<
    { jsx: JSX.Element; data: Content.Article }[]
  >([initalArticle]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsloading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const articleRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observersRef = useRef<Map<string, IntersectionObserver>>(new Map());

  const setArticleRef = useCallback(
    (id: string, slug?: string) => (el: HTMLDivElement | null) => {
      if (!el) return;
      if (!slug) return;
      if (articleRefs.current.has(id)) return;
      articleRefs.current.set(id, el);

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const nextPath = `/${slug}`;
              if (window.location.pathname !== nextPath) {
                // Pass on Next's own history state so the router doesn't sync
                // to the new URL. Otherwise the next loadMore server action
                // re-renders the page as that article and resets the feed.
                window.history.replaceState(window.history.state, "", nextPath);
              }
            }
          }
        },
        // Trigger when the article crosses the middle of the viewport, so it
        // works regardless of how tall the article is.
        { rootMargin: "-50% 0px -50% 0px" },
      );
      observer.observe(el);
      observersRef.current.set(id, observer);
    },
    [],
  );

  useEffect(() => {
    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
    };
  }, []);

  const fetchArticle = async () => {
    if (isLoading || !hasMore) return;
    setIsloading(true);

    try {
      const res = await loadMore(
        page,
        articles.map((article) => article.data.sys.id),
      );
      if (res?.length) {
        setArticles([...articles, ...res]);
      }
      setHasMore(
        res?.length === INFINITE_SCROLL_ITEMS_PER_FETCH &&
          articles.length < INFINITE_SCROLL_ARTICLE_LIMIT,
      );
      setPage(page + 1);
    } finally {
      setIsloading(false);
    }
  };

  const items = articles
    .map((article, index) => {
      const isLast = index === articles.length - 1;
      return [
        <div
          key={article.data.sys.id}
          ref={setArticleRef(
            article.data.sys.id,
            article.data.fields.slug ?? "",
          )}
        >
          <OnceInView
            callback={() => {
              if (article.data.sys.id !== initalArticle.data.sys.id) {
                kilkaya.sendPageView(article.data);
                sendGTMEvent({
                  event: "virtual_page_view",
                  page_title:
                    article.data.fields.openGraphTitle ||
                    article.data.fields.title ||
                    article.data.fields.teaserTitle,
                  page_location: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${article.data.fields.slug}`,
                  page_path: `/${article.data.fields.slug}`,
                });
              }
            }}
          >
            {article.jsx}
          </OnceInView>
        </div>,

        isLast && hasMore && (
          <div
            key={article.data.sys.id + "next"}
            className="border-b border-b-black  flex justify-center text-sm mb-16 mt-8"
          >
            <span className="border border-black  font-bold uppercase p-2 bg-sand-500  translate-y-1/2">
              Nästa Artikel
            </span>
          </div>
        ),
      ];
    })
    .flatMap((x) => x);

  items.splice(1, 0);

  return (
    <InfiniteScroll hasMore={hasMore} threshold={600} loadMore={fetchArticle}>
      {items}
    </InfiniteScroll>
  );
};

export default ArticlePageFeed;
